import { useMutation, useQuery } from '@apollo/client';
import { message } from 'antd';
import { FC } from 'react';
import {
  CasesIdentityVerificationV1ChangeStatusContainerFileInfoFieldsFragment,
  CasesIdentityVerificationV1ChangeStatusContainerIdentityVerificationCaseV1Document,
  CasesIdentityVerificationV1ChangeStatusContainerIdentityVerificationCaseV1FieldsFragment,
  CasesIdentityVerificationV1ChangeStatusContainerIdentityVerificationCaseV1UpdateDocument,
  CasesIdentityVerificationV1ChangeStatusContainerStaffUserCurrentDocument,
  CasesIdentityVerificationV1ChangeStatusContainerStaffUserFieldsFragment,
  IdentityVerificationCaseV1UpdateInput
} from '../../../../../../generated';
import { useOid } from '../../../../../../hooks/useOid';
import { ComponentQueryLoader } from '../../../../../shared/component-query-loader';
import { getStaffUserCurrentQueryOptionsWithOidAndCacheEnabled } from '../../../../../shared/utils/queryOptions';
import { ChangeStatus } from './change-status';

interface ChangeStatusContainerProps {
  caseId: string;
}

export const ChangeStatusContainer: FC<ChangeStatusContainerProps> = (props: ChangeStatusContainerProps) => {
  const oid = useOid();
  const staffUserCurrentQueryOptions = getStaffUserCurrentQueryOptionsWithOidAndCacheEnabled(oid);

  const {
    data: caseStatusData,
    loading: caseStatusLoading,
    error: caseStatusError
  } = useQuery(CasesIdentityVerificationV1ChangeStatusContainerIdentityVerificationCaseV1Document, {
    variables: {
      identityVerificationCaseV1Id: props.caseId,
      fileName: 'id-form-notarized-redacted.pdf'
    }
  });

  const {
    data: staffUserData,
    loading: staffUserLoading,
    error: staffUserError
  } = useQuery(CasesIdentityVerificationV1ChangeStatusContainerStaffUserCurrentDocument, staffUserCurrentQueryOptions);

  const [caseDataChangeStatus] = useMutation(CasesIdentityVerificationV1ChangeStatusContainerIdentityVerificationCaseV1UpdateDocument);

  const handleChangeStatus = async (values: IdentityVerificationCaseV1UpdateInput) => {
    try {
      const result = await caseDataChangeStatus({
        variables: {
          input: values
        }
      });
      if (result.data?.identityVerificationCaseV1Update.status.success !== true) {
        message.error(`Error changing case status : ${result.data?.identityVerificationCaseV1Update.status.errorMessage}`);
      } else {
        message.success('Status changed successfully');
      }
    } catch (error) {
      message.error(`Error changing case status : ${JSON.stringify(error)}`);
    }
  };

  return (
    <ComponentQueryLoader
      loading={caseStatusLoading || staffUserLoading}
      error={caseStatusError || staffUserError}
      hasData={caseStatusData?.identityVerificationCaseV1 && staffUserData?.staffUserCurrent}
      hasDataComponent={
        <ChangeStatus
          data={{
            caseData: caseStatusData?.identityVerificationCaseV1 as CasesIdentityVerificationV1ChangeStatusContainerIdentityVerificationCaseV1FieldsFragment,
            userData: staffUserData?.staffUserCurrent as CasesIdentityVerificationV1ChangeStatusContainerStaffUserFieldsFragment,
            fileData: caseStatusData?.identityVerificationCaseV1?.fileByFileName as CasesIdentityVerificationV1ChangeStatusContainerFileInfoFieldsFragment
          }}
          onChangeStatus={handleChangeStatus}
        />
      }
    />
  );
};
